import { captureException } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { ChevronLeft, Sim, Watch } from "@/assets/icons/ds";
import { AppearTransition } from "@/components/Interface/AppearTransition/AppearTransition";
import { Button } from "@/components/Interface/Button/Button";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { CheckoutPaymentCard } from "@/components/Layout/Payment/CheckoutPaymentCard/CheckoutPaymentCard";
import { LightboxWrapper } from "@/components/Layout/Payment/LightboxWrapper/LightboxWrapper";
import {
  DeductionRow,
  DueNowTotalRow,
  ThereafterRow,
} from "@/components/Layout/PriceTable/DueNowRows";
import { PriceRow } from "@/components/Layout/PriceTable/PriceRow";
import {
  SubscriptionInfoBox,
  SubscriptionInfoRow,
} from "@/components/Layout/SubscriptionInfoBox/SubscriptionInfoBox";
import { useDisplayedSim } from "@/components/Portal/DeviceManagement/hooks/useDisplayedSim.ts";
import { useOptionCheckoutSubmit } from "@/components/Portal/DeviceManagement/hooks/useOptionCheckoutSubmit";
import { getVoiceOptionEntryFromRateBasket } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionChange/Steps/SubscriptionCheckout";
import { useAlert } from "@/hooks/useAlert";
import { useHandleError } from "@/hooks/useHandleError";
import { usePaymentMode } from "@/hooks/usePaymentMode";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import {
  getAvailableSunriseRoamingPackages,
  orderRoamingPackage,
  useGetProduct,
} from "@/services/api";
import {
  Basket,
  DrCreditCardCardType,
  SubscriberOrderResponseSubscriberPackageType,
} from "@/services/model";
import { AlertTypes } from "@/utils/atoms";
import { getCreditAmount } from "@/utils/basketUtils";
import { env } from "@/utils/environmentHelpers";
import { getNextBillDate } from "@/utils/tariffUtils";
import {
  getCurrency,
  getCurrencyFromLookupValue,
} from "@/utils/translationHelpers";

type FormProps = {
  paymentProvider: DrCreditCardCardType;
};

export const AddWatchSimOption: FC = () => {
  const handleError = useHandleError();
  const { t, i18n } = useTranslation();
  const { t: tCheckout } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.watchSim.checkout",
  });
  const [simId] = useSimIdFromQueryParam();
  const paymentMode = usePaymentMode();
  const { isInvoice, isCreditCard, maskedCardNumberString, cardType } =
    paymentMode;
  const navigate = useNavigate();
  const showAlert = useAlert();
  const { control, watch } = useForm<FormProps>();

  const [optionBasket, setOptionBasket] = useState<Basket | null>(null);

  const { data: simDetail } = useDisplayedSim();

  const serviceId = simDetail?.serviceID;
  const { data: dataProduct, isPending: isLoadingProduct } = useGetProduct(
    serviceId || 0,
    {
      expanded: true,
      client: Number(env.clientNumericId ?? 1),
    },
    {
      query: {
        enabled: serviceId !== undefined && !isNaN(Number(env.clientNumericId)),
      },
    },
  );
  const voiceOptionEntry = getVoiceOptionEntryFromRateBasket(optionBasket);

  const watchOption = useMemo(
    () =>
      optionBasket?.entries?.[0]?.options?.[0]?.options?.filter(
        (option) =>
          option?.product?.id === 131701 || option?.product?.id === 131702,
      )[0],
    [optionBasket?.entries],
  );
  const watchOptionOneTimeAmount = watchOption?.oneTimeCharges?.[0]?.amount;
  // The one time data product charge, might be discounted as well
  const dataProductOneTimeCharge = useMemo(
    () =>
      voiceOptionEntry?.oneTimeCharges?.filter(
        (value) => value.chargeType?.id === 4,
      ),
    [voiceOptionEntry?.oneTimeCharges],
  );
  const dataProductCharge = useMemo(
    () =>
      dataProduct?.charges?.filter(
        (charge) => charge.id === dataProductOneTimeCharge?.[0]?.charge?.id,
      ),
    [dataProduct?.charges, dataProductOneTimeCharge],
  );
  const queryClient = useQueryClient();

  const goToWatchSimDisplay = useCallback(() => {
    navigate(`../display?simId=${simDetail?.simId}`, { replace: true });
  }, [navigate, simDetail]);

  const handleSuccess = useCallback(async () => {
    // invalidate queries to refetch sim details
    queryClient.invalidateQueries({ queryKey: ["/sim/sim"] });
    // Added in order to trigger a refetching in the DeviceManagement
    showAlert({
      type: AlertTypes.success,
      text: t("cockpit.managementTile.roaming.checkout.success"),
    });

    goToWatchSimDisplay();
  }, [goToWatchSimDisplay, queryClient, showAlert, t]);

  // Clearing the basket makes the effect below order a fresh one.
  const resetOptionBasket = useCallback(() => setOptionBasket(null), []);
  const discountAmount = optionBasket?.total?.oneTimeCampaign?.amount;
  const credit = getCreditAmount(optionBasket?.balance);
  const creditTimeRemaining =
    optionBasket?.total?.oneTime?.amountDiscounts || 0;
  // substract discount and credit to achieve totalCost - attention! creditTimeRemaining is substracted in backend already
  const totalOnceAmount =
    (optionBasket?.total?.oneTime?.amount || 0) -
    (discountAmount || 0) -
    credit;

  const {
    submit,
    datatransRequestDocument,
    isCreatingTransaction,
    isAddingOptionLoading,
    isAddingOptionDone,
  } = useOptionCheckoutSubmit({
    domain: "AddWatchSimOption",
    simId,
    basket: optionBasket,
    refreshBasket: resetOptionBasket,
    paymentMode,
    onSuccess: handleSuccess,
  });

  useEffect(() => {
    // Only initialize a new basket if theres none in the optionBasketAtom
    if (!optionBasket) {
      if (typeof serviceId === "number" && simId) {
        // Get available packages for this sim
        getAvailableSunriseRoamingPackages(parseInt(simId), {
          packageType: SubscriberOrderResponseSubscriberPackageType.Watch_Sim,
        })
          .then((data) => {
            const watchPackageId = data?.subscriberPackagesWatch?.[0].id;
            if (watchPackageId === undefined) {
              throw new Error(
                `No watch package found for serviceId ${serviceId}`,
              );
            }

            return orderRoamingPackage(parseInt(simId), {
              optionId: serviceId,
              watchId: watchPackageId,
            });
          })
          .then((data) => {
            if (!data?.basket) {
              throw new Error("Empty basket response from orderRoamingPackage");
            }

            setOptionBasket(data.basket);
          })
          .catch((error) => {
            // Without a basket the page would spin forever, so leave the checkout.
            captureException(error);
            handleError(error);
            goToWatchSimDisplay();
          });
      }
    }
  }, [optionBasket, serviceId, simId, goToWatchSimDisplay, handleError]);

  const watchedPaymentProvider = watch("paymentProvider");
  const disableSuccessButton =
    isCreatingTransaction ||
    isAddingOptionLoading ||
    isAddingOptionDone ||
    !(watchedPaymentProvider || isInvoice || isCreditCard);
  const currency = getCurrencyFromLookupValue(
    optionBasket?.currency || { id: 1 },
  );
  const isLoading =
    isLoadingProduct ||
    optionBasket === null ||
    isCreatingTransaction ||
    isAddingOptionLoading;

  const flatName = dataProduct?.baseDisplayValue;
  const flatPrice = dataProductCharge?.[0]?.amount;
  // The campaign discount's own name (e.g. "Friends with Benefits 10%");
  // the generic "Discount" only when the basket carries none.
  const discountName =
    voiceOptionEntry?.discounts?.[0]?.discountName ??
    t("Onboarding.sections.checkout.summary.discount");
  const totalRecurringAmount = optionBasket?.total?.recurring?.amount;
  const nextBillDate = getNextBillDate(simDetail, i18n.language);
  const billingDays = simDetail?.minContractDuration ?? 30;

  // simDetail should always be present. This is to prevent TypeScript warnings.
  if (!simDetail) {
    return null;
  }

  return (
    <section data-testid="add-watch-sim-option" className="w-full">
      <CardContainer
        className="flex w-full flex-col gap-6 rounded-lg p-6"
        variant="ivory"
      >
        <Button
          dataTestid="watch-sim-checkout-back"
          variant="tertiary"
          ariaLabel={t("Common.label.back")}
          icon={<ChevronLeft />}
          onClick={goToWatchSimDisplay}
        />

        <div className="flex w-full flex-col gap-4">
          <h1 className="text-headline-xl font-semibold leading-xs text-foreground-primary">
            {tCheckout("title")}
          </h1>
          <p className="text-body-base leading-none text-foreground-secondary">
            <Trans
              t={t}
              values={{ count: simDetail.remainingDays ?? 1 }}
              components={[<span key="boldTime" className="font-semibold" />]}
            >
              cockpit.managementTile.watchSim.checkout.autoRenewal.hint
            </Trans>
          </p>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <AppearTransition>
            <TwoColumnLayout
              independentColumns
              rows={[
                {
                  left: (
                    <CardModule
                      dataTestid="watch-sim-checkout-tariff"
                      variant="white"
                      title={t(
                        "portal:cockpit.managementTile.tariff.yourTariff",
                      )}
                    >
                      <div className="flex w-full flex-col gap-3">
                        <div className="flex h-14 w-full flex-col justify-center rounded-md bg-surface-quaternary px-5 text-foreground-tertiary">
                          <p className="text-body-xs leading-md">
                            {t("Common.label.subscriptionName")}
                          </p>
                          <p className="truncate-safe text-body-base font-semibold leading-md">
                            {simDetail.alias ?? flatName}
                          </p>
                        </div>

                        <div className="flex w-full flex-col gap-2">
                          <SubscriptionInfoBox>
                            <SubscriptionInfoRow
                              icon={<Sim className="size-6" />}
                              title={flatName ?? ""}
                            />
                          </SubscriptionInfoBox>

                          <SubscriptionInfoBox>
                            <SubscriptionInfoRow
                              icon={<Watch className="size-6" />}
                              title={t("Common.products.watchSim")}
                            />
                          </SubscriptionInfoBox>
                        </div>
                      </div>
                    </CardModule>
                  ),
                  right: (
                    <CardModule
                      dataTestid="watch-sim-checkout-due"
                      className="self-start"
                      variant="white"
                      title={t("portal:common.checkout.nowToPay")}
                    >
                      <div className="flex w-full flex-col">
                        <PriceRow
                          withBackdrop
                          variant="strong"
                          label={flatName}
                          value={
                            flatPrice != null
                              ? getCurrency(flatPrice, currency)
                              : ""
                          }
                        />

                        {!!discountAmount && discountAmount > 0 && (
                          <DeductionRow
                            dataTestid="watch-sim-checkout-discount"
                            variant="strong-xs"
                            label={discountName}
                            amount={discountAmount}
                          />
                        )}

                        {watchOptionOneTimeAmount != null && (
                          <PriceRow
                            label={t("Common.products.watchSim")}
                            value={
                              <span data-testid="watch-sim-checkout-watch-option">
                                {getCurrency(
                                  watchOptionOneTimeAmount,
                                  currency,
                                )}
                              </span>
                            }
                          />
                        )}

                        {creditTimeRemaining > 0 && (
                          <DeductionRow
                            dataTestid="watch-sim-checkout-credit-time-remaining"
                            variant="strong-xs"
                            label={tCheckout("creditRemainingTime")}
                            amount={creditTimeRemaining}
                          />
                        )}

                        {credit > 0 && (
                          <DeductionRow
                            dataTestid="watch-sim-checkout-credit"
                            withDivider
                            variant="strong-xs"
                            label={t("portal:common.checkout.creditOther")}
                            amount={credit}
                          />
                        )}

                        <DueNowTotalRow
                          dataTestid="watch-sim-checkout-total"
                          label={t("portal:common.checkout.sum")}
                          amount={totalOnceAmount}
                          currency={currency}
                          caption={tCheckout("today")}
                          withTopDivider={false}
                        />

                        {totalRecurringAmount != null && (
                          <ThereafterRow
                            dataTestid="watch-sim-checkout-recurring"
                            amount={totalRecurringAmount}
                            currency={currency}
                            date={
                              nextBillDate &&
                              t("Common.checkout.dueOn", { date: nextBillDate })
                            }
                            billingPeriod={t(
                              "portal:cockpit.managementTile.tariff.payInterval",
                              { days: billingDays },
                            )}
                          />
                        )}
                      </div>
                    </CardModule>
                  ),
                },
                {
                  left: (
                    <CheckoutPaymentCard
                      dataTestid="watch-sim-checkout-payment"
                      isInvoice={isInvoice}
                      isCreditCard={!!isCreditCard}
                      cardType={cardType}
                      maskedCardNumberString={maskedCardNumberString}
                      control={control}
                    />
                  ),
                },
              ]}
            />

            <LightboxWrapper
              paymentProvider={watchedPaymentProvider}
              transactionId={datatransRequestDocument?.refNo}
              sign={datatransRequestDocument?.sign}
              enabled={datatransRequestDocument !== undefined}
            />

            <div className="mt-6 flex w-full justify-end">
              <Button
                dataTestid="watch-sim-checkout-submit"
                disabled={disableSuccessButton}
                onClick={() => submit(totalOnceAmount)}
              >
                {tCheckout("confirm")}
              </Button>
            </div>
          </AppearTransition>
        )}
      </CardContainer>
    </section>
  );
};
